// components
import { Form, Input, Col, Button } from "antd";
import Checkbox from "components/atoms/Checkbox/Checkbox";
import FirstTimeLoginWrapper from "components/molecules/Login/Login";
import ErrorMessage from "components/molecules/ErrorMessage/ErrorMessage";
import { Head } from "components/molecules/Head/Head";

// styles
import styles from "containers/FirstTimeLogin/firstTimeLogin.module.css";

// constants
import { FirstTimeLogin as Constants, LoginPage, PageTitles } from "constants/global";

// hooks
import { FormDataValues, WatchWordValidationState } from "containers/FirstTimeLogin/useFirstTimeLogin";

type ComponentProps = {
	firstTimeLoginProps: {
		watchWord: string;
		watchWordChecks: {
			isMinChars: boolean;
			isUpperCase: boolean;
			isLowerCase: boolean;
			isSpecialChar: boolean;
		};
		confirmWatchword: string;
		isDetailsValid: boolean;
		isValidWatchWord: WatchWordValidationState;
		onWatchwordChange: (value: string) => void;
		onWatchwordBlur: () => void;
		onChangePW: (values: FormDataValues) => void;
		setConfirmWatchword: (value: string) => void;
		backToLogin: () => void;
	};
};

const FirstTimeLogin = ({ firstTimeLoginProps }: ComponentProps) => {
	const [form] = Form.useForm();
	const { watchWord, watchWordChecks, backToLogin, confirmWatchword, isValidWatchWord, isDetailsValid, onWatchwordBlur, onWatchwordChange, onChangePW, setConfirmWatchword } =
		firstTimeLoginProps;

	const passwordStyles = isValidWatchWord === "invalid" ? styles?.err : styles?.filled;

	return (
		<FirstTimeLoginWrapper page="reset" isDisclaimer={true} disclaimerText={Constants?.DISCLAIMER_TEXT}>
			<div className={styles?.login_form}>
				<Head>{PageTitles?.FIRST_TIME_LOGIN}</Head>
				<Form form={form} name="register" onFinish={onChangePW} initialValues={{}} scrollToFirstError>
					<Form.Item
						name="password"
						label=""
						extra={isValidWatchWord === "invalid" && <ErrorMessage classes="text-red-500" message={Constants.INVALID_WATCHWORD} />}
						rules={[
							{
								required: true,
								message: <ErrorMessage message={Constants.ERR_WATCHWORD} />
							}
						]}
					>
						<Input.Password
							value={watchWord}
							onBlur={() => onWatchwordBlur()}
							onChange={(e) => onWatchwordChange(e?.target?.value)}
							placeholder={Constants.WATCHWORD_PLACEHOLDER}
							className={`${styles?.new_password} ${watchWord ? passwordStyles : ""}`}
						/>
					</Form.Item>
					<Col span={32} className={styles?.center}>
						<Checkbox
							id="isMinChar"
							name="passwordChecks"
							checked={watchWordChecks?.isMinChars}
							classes={{
								checkbox_checked: styles?.checkbox_checked,
								checkbox_input: styles?.checkbox_input,
								checkbox_label: styles?.checkbox_label
							}}
							label={Constants?.CB_LABEL1}
							value="isMinChars"
						/>
					</Col>
					<Col span={32} className={styles?.center}>
						<Checkbox
							id="isUpperCase"
							name="passwordChecks"
							checked={watchWordChecks?.isUpperCase}
							classes={{
								checkbox_checked: styles?.checkbox_checked,
								checkbox_input: styles?.checkbox_input,
								checkbox_label: styles?.checkbox_label
							}}
							label={Constants?.CB_LABEL2}
							value="isUpperCase"
						/>
					</Col>
					<Col span={32} className={styles?.center}>
						<Checkbox
							id="isLowerCase"
							name="passwordChecks"
							checked={watchWordChecks?.isLowerCase}
							classes={{
								checkbox_checked: styles?.checkbox_checked,
								checkbox_input: styles?.checkbox_input,
								checkbox_label: styles?.checkbox_label
							}}
							label={Constants?.CB_LABEL3}
							value="isLowerCase"
						/>
					</Col>
					<Col span={32} className={styles?.center}>
						<Checkbox
							id="isSpecialChar"
							name="passwordChecks"
							checked={watchWordChecks?.isSpecialChar}
							classes={{
								checkbox_checked: styles?.checkbox_checked,
								checkbox_input: styles?.checkbox_input,
								checkbox_label: styles?.checkbox_label
							}}
							label={Constants?.CB_LABEL4}
							value="isSpecialChar"
						/>
					</Col>

					<Form.Item
						name="confirm_password"
						label=""
						dependencies={["password"]}
						rules={[
							{
								required: true,
								message: <ErrorMessage message={Constants.ERR_CONFIRM_WATCHWORD} />
							},
							({ getFieldValue }) => ({
								validator(_, value) {
									if (!value || getFieldValue("password") === value) {
										return Promise.resolve();
									}
									return Promise.reject(new Error(Constants.ERR_WATCHWORD_MATCH));
								}
							})
						]}
					>
						<Input.Password
							placeholder={Constants.CONFIRM_WATCHWORD_PLACEHOLDER}
							onChange={(e) => setConfirmWatchword(e.target?.value)}
							value={confirmWatchword}
							className={`${styles?.confirm_new_password} ${confirmWatchword ? styles?.filled : ""}`}
						/>
					</Form.Item>

					<Form.Item className="flex items-center justify-center mb-12">
						<Button className={isDetailsValid ? styles?.submit_button : styles?.submit_button_disabled} type="primary" htmlType="submit">
							{Constants.SUBMIT_BTN_TEXT}
						</Button>
						<Button className={styles?.link_button} type="link" onClick={backToLogin}>
							{LoginPage.BACK_TO_LOGIN}
						</Button>
					</Form.Item>
				</Form>
			</div>
		</FirstTimeLoginWrapper>
	);
};

export default FirstTimeLogin;
